<template>
    <form class="form-horizontal">
        <b-alert
            show
            variant="warning"
            v-if="
                data.isDefaultProvider &&
                data.bundle_id.length > 0 &&
                page != 'checkin'
            "
        >
            <span id="dialog-alert-message">
                {{ CLINIC_PROVIDER_ALERT.LAB_RADIOLOGY_BUNDLE }}
            </span>
        </b-alert>
        <b-form-group
            label="Provider:"
            class
            label-cols="3"
            label-for="staff_id"
        >
            <select
                id="staff_id"
                v-model="data.staff_id"
                class="form-control"
                v-validate="'excluded:0'"
                :class="{
                    'border-danger':
                        errors.has('staff_id') || !checkStaffIdInProviders()
                }"
                name="staff_id"
            >
                <option value="0">Provider (required)</option>
                <option
                    v-for="provider in providers"
                    :key="provider.id"
                    :value="provider.id"
                    >{{ provider.providerName }}</option
                >
            </select>
        </b-form-group>
        <b-form-group
            label="Client:"
            class
            label-cols="3"
            label-for="client_id"
        >
            <v-select
                id="client_id"
                ref="vselect"
                :options="cOptions"
                v-validate="'required'"
                :class="{
                    'border-danger': errors.has('client_id'),
                    rounded: true
                }"
                v-model="data.client"
                @search:focus="clientFocus"
                @input="clientChange"
                placeholder="Client Name (required)"
                name="client_id"
                label="label"
            />
        </b-form-group>
        <b-form-group
            label="Patient:"
            class
            label-cols="3"
            label-for="patient_id"
        >
            <input
                :value="data.patient ? data.patient.name : null"
                type="text"
                placeholder="Patient Name (optional)"
                readonly="readonly"
                class="form-control"
            />
        </b-form-group>
        <b-form-group
            v-if="triageOptions.length > 0"
            label="Triage:"
            class
            label-cols="3"
            label-for="triage_type_id"
        >
            <select
                id="triage_type_id"
                v-model="data.triage_type_id"
                class="form-control"
                name="triage_type_id"
            >
                <option
                    v-for="triage in triageOptions"
                    :key="triage.id"
                    :value="triage.id"
                    >{{ triage.triage }}</option
                >
            </select>
        </b-form-group>
        <b-form-group
            v-if="$root.setting && _.includes([1, 2], $root.setting.location)"
            label="Location:"
            class
            label-cols="3"
            label-for="location"
        >
            <select id="location" v-model="data.location" class="form-control">
                <option :value="null" v-if="$root.setting.location == 2"
                    >In Clinic</option
                >
                <option
                    v-for="location in locations"
                    :key="location.id"
                    :value="location"
                    >{{ location.name }}{{ location.address }}</option
                >
            </select>
        </b-form-group>
        <b-form-group
            label="Referred By:"
            class
            label-cols="3"
            label-for="referrer"
        >
            <vet-select
                id="referrer"
                v-model="data.referrer"
                :disabled="!data.patient"
                @input="referrerChange"
                module="referrer"
                placeholder="Select referral source (optional)"
                name="referrer"
            />
        </b-form-group>
        <b-form-group
            label="Complaint:"
            class
            label-cols="3"
            label-for="reason"
        >
            <input
                id="reason"
                v-validate="'required'"
                :class="{ 'border-danger': errors.has('reason') }"
                v-model="data.reason"
                type="text"
                class="form-control"
                name="reason"
                placeholder="Complaint (required)"
            />
        </b-form-group>
        <b-form-group label="Weight:" class label-cols="3" label-for="weight">
            <div class="form-group row no-gutters">
                <input
                    id="weight"
                    v-validate="'decimal'"
                    :class="{ 'border-danger': errors.has('weight') }"
                    v-model="data.weight"
                    :disabled="!data.patient"
                    type="text"
                    class="form-control col-6 mr-2"
                    name="weight"
                    placeholder="Weight (optional)"
                />
                <b-form-select
                    v-model="data.uom"
                    :options="weight_units"
                    :disabled="!data.patient"
                    text-field="value"
                    name="uom"
                    value-field="id"
                    class="col form-control"
                ></b-form-select>
            </div>
        </b-form-group>
        <b-form-group
            label="Print Letters:"
            class
            label-cols="3"
            label-for="templates"
        >
            <v-select
                id="templates"
                ref="vselect"
                :options="_.difference(templates, data.letter_ids)"
                v-model="data.letter_ids"
                :disabled="!data.patient"
                placeholder="Select template (optional)"
                multiple
                label="name"
            />
        </b-form-group>
        <b-form-group
            v-if="data.letter_ids.length > 0"
            label="Author:"
            class
            label-cols="3"
            label-for="author_id"
        >
            <select
                id="author_id"
                v-model="data.author_id"
                class="form-control"
                v-validate="'excluded:0'"
                :class="{
                    'border-danger':
                        errors.has('author_id') || !checkAuthorIdInAuthors()
                }"
                name="author_id"
            >
                <option value="0">Author (required)</option>
                <option
                    v-for="author in authors"
                    :key="author.id"
                    :value="author.id"
                    >{{ author.authorName }}</option
                >
            </select>
        </b-form-group>
        <b-form-group
            label="Send Forms:"
            class
            label-cols="3"
            label-for="send-forms"
        >
            <v-select
                id="send-forms"
                v-model="data.webforms"
                label="name"
                multiple
                :options="formTemplateOptions"
                :disabled="!data.patient"
                :placeholder="
                    isLoadingWebformTemplates
                        ? 'Loading... '
                        : 'Select forms (optional)'
                "
            >
                <template v-slot:list-footer>
                    <li v-if="hasNextPageWebfromTemplates">
                        Loading...
                    </li>
                </template>
                <template v-slot:no-options>
                    <li v-if="isLoadingWebformTemplates">
                        Loading...
                    </li>
                </template>
            </v-select>
        </b-form-group>
        <div v-if="data.webforms.length > 0" class="form-group form-row">
            <label for="send_forms_via" class="col-3 col-form-label">
                <i class="fa fa-caret-right"></i> Send via:
            </label>
            <div class="bv-no-focus-ring col">
                <v-select
                    name="send-via"
                    v-model="selectedCommunicationType"
                    :options="sendViaOptions"
                    ref="send-via"
                    :selectable="option => !option.disabled"
                    :searchable="false"
                    :clearable="false"
                    @input="onSelectionChange"
                    :class="{ 'border-danger': errors.has('send-via') }"
                    v-validate="'required'"
                >
                    <template slot="option" slot-scope="option">
                        <div
                            v-if="
                                option.value === COMMUNICATION_TYPES.SMS &&
                                option.disabled
                            "
                            v-b-tooltip.hover="{
                                placement: 'left',
                                title:
                                    CCLIENT.ALERT_MESSAGE.EXTENSION_SMS_DISABLED
                            }"
                        >
                            {{ option.label }}
                        </div>
                        <div v-else>{{ option.label }}</div>
                    </template>
                </v-select>
            </div>
        </div>
        <div
            v-if="
                data.webforms.length > 0 &&
                (isCommunicationTypeEmail || isCommunicationTypeSMS)
            "
            class="form-group form-row"
        >
            <label for="send_forms_via" class="col-3 col-form-label">
                <i class="fa fa-caret-right"></i> To:
            </label>
            <div class="bv-no-focus-ring col">
                <input
                    v-show="isCommunicationTypeEmail"
                    v-validate="
                        isCommunicationTypeEmail ? 'email|required' : ''
                    "
                    :class="{ 'border-danger': errors.has('email') }"
                    v-model="data.email"
                    type="text"
                    class="form-control mr-2"
                    name="email"
                    placeholder="Email (required)"
                />
                <b-row v-show="isCommunicationTypeSMS">
                    <b-col cols="4" class="pr-0">
                        <country-code
                            class="form-control"
                            v-model="data.mobileCountryCode"
                            :defaultValue="$root.setting.phone_code"
                        />
                    </b-col>
                    <b-col class="pl-1">
                        <b-form-input
                            v-model="data.mobile"
                            v-validate="
                                isCommunicationTypeSMS
                                    ? 'required|international_phone'
                                    : ''
                            "
                            name="mobile"
                            :class="{
                                'border-danger': errors.has('mobile')
                            }"
                            placeholder="Mobile Phone (required)"
                        ></b-form-input>
                    </b-col>
                </b-row>
                <b-form-checkbox
                    id="isUpdateClientContact"
                    v-model="data.isUpdateClientContact"
                    name="isUpdateClientContact"
                    plain
                    class="col-form-label pt-1"
                >
                    <span class="fw-normal"
                        >Update client contact information</span
                    >
                </b-form-checkbox>
            </div>
        </div>
        <b-form-group
            v-if="_isExtensionEnabled('TREATMENT_BOARD')"
            label="Admit Patient:"
            class
            label-cols="3"
            label-for="admit"
        >
            <select
                id="admit"
                v-model="data.admit"
                :disabled="!data.patient"
                class="form-control"
            >
                <option :value="false">Do not admit patient</option>
                <option :value="true">Admit patient</option>
            </select>
            <b-form-checkbox
                v-if="data.admit"
                id="startTreatment"
                v-model="data.startTreatment"
                name="startTreatment"
                plain
                class="col-form-label pt-1"
            >
                <span class="fw-normal">Start treatment</span>
            </b-form-checkbox>
        </b-form-group>
        <b-form-group
            label="Medical Note:"
            class
            label-cols="3"
            label-for="soap"
        >
            <select
                id="soap"
                v-model="data.soap_id"
                :disabled="!data.patient"
                class="form-control"
            >
                <option :value="null">Do not create medical note</option>
                <option
                    v-for="soap in sortedSoaps"
                    :key="soap.id"
                    :value="soap.id"
                    >{{ soap.name }}</option
                >
            </select>
        </b-form-group>
        <b-form-group label="Bundle:" class label-cols="3" label-for="bundle">
            <select
                v-if="data.admit"
                id="bundle"
                ref="bundle"
                v-model="data.single_bundle_id"
                v-validate="''"
                :disabled="!data.patient"
                name="bundle"
                class="form-control"
            >
                <option value>No Bundle</option>
                <option
                    v-for="bundle in bundles"
                    :key="bundle.id"
                    :value="bundle.id"
                    >{{ bundle.name }}</option
                >
            </select>
            <v-select
                v-if="!data.admit"
                :disabled="!data.patient"
                id="bundle"
                ref="vselect"
                :options="bundle_options_exclude"
                v-model="data.bundle_id"
                placeholder="Select bundle (optional)"
                multiple
                label="name"
            />
        </b-form-group>
        <b-form-group
            v-if="data.id"
            label="Billing:"
            class
            label-cols="3"
            label-for="bill_to_invoice"
        >
            <select
                id="bill_to_invoice"
                v-model="data.bill_to_invoice"
                :class="{ 'border-danger': errors.has('bill_to_invoice') }"
                v-validate="
                    data.bundle_id.length > 0 || data.single_bundle_id
                        ? 'excluded:-3'
                        : ''
                "
                name="bill_to_invoice"
                class="form-control"
            >
                <option
                    :value="-3"
                    v-if="data.bundle_id.length > 0 || data.single_bundle_id"
                    >Select invoice or estimate (required)</option
                >
                <option :value="-3" v-else
                    >Select invoice or estimate (optional)</option
                >
                <option :value="bill" v-for="bill in billings" :key="bill.id">{{
                    billingNote(bill)
                }}</option>
                <option :value="-1">New Invoice</option>
                <option :value="-2">New Estimate</option>
            </select>
        </b-form-group>
        <b-form-group
            v-if="isVetterPrintEnabled && data.patient"
            class="mt-1 mb-2"
            label-cols="3"
            label-for="print_card"
            label="Cage Card:"
        >
            <select
                id="print_card"
                :key="`print_card-${data.id}`"
                v-model="data.print_card"
                @change="disablePrintQuantity"
                name="print_card"
                class="form-control col-md-6"
                style="display: inline-block;"
            >
                <option value="no">Do not print cage card</option>
                <option
                    v-for="printer in printers"
                    :key="printer.id"
                    :value="printer.id"
                    >{{ printer.name }}</option
                >
                <!-- <option value="local">Generate PDF</option> -->
            </select>
            X
            <input
                id="print_quantity"
                :disabled="disabledPrintQuantity"
                v-model="data.print_quantity"
                v-validate="'numeric|required'"
                :class="{ 'border-danger': errors.has('print_quantity2') }"
                placeholder="Quantity (required)"
                type="number"
                min="0"
                step="1"
                class="form-control col-md-4"
                name="print_quantity2"
                value="1"
                style="display: inline-block;"
            />
        </b-form-group>
        <b-form-group
            v-else-if="!!data.patient"
            label="Cage Card:"
            class
            label-cols="3"
            label-for="print_card"
        >
            <b-form-checkbox
                id="print_card"
                :value="true"
                v-model="data.print_card"
                plain
                name="print_card"
                class="mt-1"
                >Print Cage Card</b-form-checkbox
            >
        </b-form-group>
        <b-form-group
            v-if="!isVetterPrintEnabled && data.patient"
            label="Print Quantity:"
            class
            label-cols="3"
            label-for="print_quantity"
        >
            <input
                id="print_quantity"
                :disabled="!data.print_card"
                v-model="data.print_quantity"
                :class="{ 'border-danger': errors.has('print_quantity') }"
                v-validate="'numeric|required'"
                placeholder="Quantity (required)"
                type="number"
                min="0"
                step="1"
                class="form-control"
                name="print_quantity"
                value="1"
            />
        </b-form-group>
        <b-form-group label="Notes:" class label-cols="3" label-for="notes">
            <editable
                id="notes"
                v-model="data.note"
                placeholder="Notes (optional)"
                rows="3"
                :defaultHeight="64"
            ></editable>
        </b-form-group>
    </form>
</template>
<script>
// -- Use 'module' to refer root path of src (check webpack config {resolve->alias})
import CCLIENT from "@/constants/clients";
import EXTENSIONS from "@/constants/extensions";
import { COMMUNICATION_TYPES } from "@/constants/communication-types";
import CountryCode from "@/module/commons/countrycode.vue";
import vetSelect from "@/module/commons/vet-select.vue";
import vSelect from "vue-select";
import moment from "moment-timezone";
import { Promise } from "q";
import {
    assign,
    map,
    get,
    union,
    sortBy,
    find,
    differenceBy,
    uniqBy,
    concat,
    isEmpty
} from "lodash";
import CLINICPROVIDER from "@/constants/clinic-provider";
import TemplateService from "@/services/templates";
import TEMPLATES from "@/constants/templates";
import TriageService from "@/services/triage";
import Editable from "@/components/elements/editable-textarea";

export default {
    components: {
        vetSelect,
        vSelect,
        CountryCode,
        Editable
    },
    props: {
        data: {
            type: Object,
            default: () => {}
        },
        parentLoading: {
            type: Boolean,
            default: false
        },
        page: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            CCLIENT: CCLIENT,
            COMMUNICATION_TYPES: COMMUNICATION_TYPES,
            loading: false,
            resources: {},
            locations: [],
            templates: [],
            formTemplates: [],
            bundles: [],
            soaps: [],
            providers: [],
            cOptions: [],
            invoices: [],
            estimates: [],
            weight: null,
            weight_units: [],
            printers: [],
            disabledPrintQuantity: true,
            datePickerOptions: {
                format: "DD/MM/YYYY"
            },
            timePickerOptions: {
                format: "hh:mm A"
            },
            basic_soap: [
                { id: "a", name: "Basic SOAP" },
                { id: "b", name: "Simple SOAP" },
                { id: "c", name: "Standard SOAP" }
            ],
            providerCount: 1,
            check_in_billing: "",
            CLINIC_PROVIDER_ALERT: CLINICPROVIDER.CLINIC_PROVIDER_ALERT,
            authors: [],
            isLoadingWebformTemplates: true,
            hasNextPageWebfromTemplates: false,
            sendViaOptions: [
                { value: 0, label: "Do not send form" },
                { value: COMMUNICATION_TYPES.EMAIL, label: "Email" },
                {
                    value: COMMUNICATION_TYPES.SMS,
                    label: "SMS (Text)",
                    disabled: !this.checkExtensionsSMSEnabled()
                }
            ],
            selectedCommunicationType: null,
            triageOptions: []
        };
    },
    computed: {
        billings() {
            return union(
                map(this.invoices, array => assign({}, array, { type: -1 })),
                map(this.estimates, array => assign({}, array, { type: -2 }))
            );
        },
        sortedSoaps() {
            return sortBy(this.soaps, ["name"]);
        },
        bundle_options_exclude() {
            return _.difference(this.bundles, this.data.bundle_id);
        },
        isBundle() {
            if (this.data.admit) {
                return !this.data.single_bundle_id;
            } else {
                if (this.data.bundle_id.length > 0) {
                    return false;
                }

                return true;
            }
        },
        isVetterPrintEnabled() {
            return this._isExtensionEnabled("VETTER_PRINTER");
        },
        formTemplateOptions() {
            return differenceBy(this.formTemplates, this.data.webforms, "id");
        },
        isCommunicationTypeEmail() {
            return (
                this.selectedCommunicationType &&
                this.selectedCommunicationType.value ===
                    COMMUNICATION_TYPES.EMAIL
            );
        },
        isCommunicationTypeSMS() {
            return (
                this.selectedCommunicationType &&
                this.selectedCommunicationType.value === COMMUNICATION_TYPES.SMS
            );
        }
    },
    watch: {
        "data.client_id": {
            handler(newData) {
                if (newData) {
                    this.getBilling(newData);
                    this.getLocations(newData);
                    this.getReferrer(newData.id).then(referrer => {
                        this.data.referrer = referrer[0];
                        this.data.referral_id = this._bindReferrer(
                            this.data.referrer
                        );
                    });
                } else {
                    this.invoices = [];
                    this.estimates = [];
                }
            }
        },
        "data.patient_id": {
            handler(newData) {
                if (newData) {
                    this.getWeight(newData);
                } else {
                    this.data.weight = null;
                    this.data.weight_unit = 75;
                }
            }
        },
        "data.appointmentType"(value) {
            this.setDefault(value);
        },
        // "data.admit"(value) {
        //     if (value) {
        //         this.data.bundle_id = "";
        //     }
        // },
        "data.staff_id"(value, old) {
            if (value !== "") {
                this.setStaffId(this.providers);
            }
        },
        "data.admit"(value) {
            if (value) {
                this.bundle_data = {
                    bundle_id: null,
                    selectedElements: [],
                    elements: []
                };
            }
        },
        "data.author_id"(value, old) {
            if (value !== "") {
                this.setAuthorId(this.authors);
            }
        },
        "data.client"(value) {
            this.setSendWebformViaField(this.selectedCommunicationType, value);
        }
    },
    created() {},
    mounted() {
        this.loading = true;
        this.client_id = this.data.client_id;
        this.datePickerOptions.format = this.getDateFormat();
        this.$nextTick(() => {
            // put all initialize process into promise
            this.$emit("beforeload");

            let process = [
                this.getProviders(),
                this.getTemplate(),
                this.getWebformTemplates(),
                this.getSoap(),
                this.getBundle(),
                this.getWeightUnits(),
                this.getAuthors(),
                this.getTriageList()
            ];

            if (this.client_id) {
                process.push(this.getBilling(this.client_id));
                process.push(this.getLocations(this.client_id));
            }
            this.$emit("loading");
            Promise.all(process).then(() => {
                this.$emit("loaded");
            });

            this.$validator.reset();
            this.loading = false;
        });

        if (this._isExtensionEnabled("VETTER_PRINTER")) {
            this.getPrinters();
        }

        /**
         * only reset bill_to_invoice data if bill_to_invoice is empty or is set to -3
         * without doing so, bill_to_invoice loses its selected value when user clicks back from bundle view
         */
        if (!this.data.bill_to_invoice || this.data.bill_to_invoice === -3) {
            switch (this.$root.setting.check_in_billing) {
                case 1:
                    this.data.bill_to_invoice = -2;
                    break;
                case 2:
                    this.data.bill_to_invoice = -1;
                    break;
                case 3:
                    this.check_in_billing = this.$root.setting.check_in_billing;
                    break;
                default:
                    this.data.bill_to_invoice = -3;
                    break;
            }
        }
        this.setDefault(this.data.appointmentType);
    },
    methods: {
        checkExtensionsSMSEnabled() {
            return (
                this._isExtensionEnabled(EXTENSIONS.CODE.CODE_SMS) ||
                this._isExtensionEnabled(EXTENSIONS.CODE.CODE_2_WAY_MESSAGING)
            );
        },
        onSelectionChange(selectedOption) {
            if (selectedOption && selectedOption.disabled) {
                // Resetting the selection because IF selectedOption.disabled == TRUE, THEN user should not be able to select
                // Reason of overwrite : vue-select component still enable user to search and then select the option by enter/click the option although it has been disabled.
                this.selectedCommunicationType = null;
                return;
            }
            this.data.communicationType = selectedOption.value;
            this.setSendWebformViaField(selectedOption, this.data.client);
        },
        async getPrinters() {
            let response = await this.$http.get(
                process.env.ACCOUNT_SERVICE + "printer"
            );
            if (response) {
                this.printers = response.data.response.resources;
            }

            if (!this.data.print_card) {
                this.data.print_card = "no";
            }
        },
        disablePrintQuantity() {
            if (
                !this.data.print_card ||
                this.data.print_card == "no" ||
                this.data.print_card == "local"
            ) {
                this.disabledPrintQuantity = true;
            } else {
                this.disabledPrintQuantity = false;
            }
        },
        getTemplate() {
            let self = this;
            return this.$http
                .get(process.env.ACCOUNT_SERVICE + "template/letter")
                .then(function (response) {
                    self.templates = response.data.response.resources;
                });
        },
        async getWebformTemplates(page = 0) {
            this.isLoadingWebformTemplates = true;

            let params = {
                perPage: 500,
                page: page,
                orderBy: "name",
                order: "asc",
                type: parseInt(TEMPLATES.TYPE.WEBFORM_TEMPLATE)
            };
            const results = await TemplateService.getTemplates(params);
            this.formTemplates = uniqBy(
                concat(this.formTemplates, results.resources),
                "id"
            );
            this.hasNextPageWebfromTemplates =
                results.meta.currentPage < results.meta.lastPage;
            if (this.hasNextPageWebfromTemplates) {
                await this._sleep(100);
                this.getWebformTemplates(++page);
            }

            this.isLoadingWebformTemplates = false;
        },
        getBundle() {
            let self = this;
            return this.$http
                .get(process.env.ACCOUNT_SERVICE + "template/bundle")
                .then(function (response) {
                    self.bundles = response.data.response.resources;

                    self.setDefault(self.data.appointmentType);
                });
        },
        getSoap() {
            let self = this;
            return this.$http
                .get(process.env.ACCOUNT_SERVICE + "template/soap")
                .then(function (response) {
                    self.soaps = response.data.response.resources;
                });
        },
        getWeightUnits() {
            let self = this;
            return this.$http
                .get(process.env.ACCOUNT_SERVICE + "setting/masteruom")
                .then(response => {
                    self.weight_units = response.data.response.resource;
                    self.data.uom = get(self, "weight_units[0].id", 75);
                });
        },
        getProviders() {
            let self = this;
            return this.$http
                .get(process.env.BARRAMUNDI + "actor/staff/business/providers")
                .then(function (response) {
                    self.providers = get(
                        response,
                        "data.response.resource.data",
                        []
                    );

                    self.providers = _.map(self.providers, element => ({
                        ...element,
                        id: element.actor.id,
                        providerName:
                            element.actor.firstname +
                            " " +
                            element.actor.lastname
                    }));

                    // --- sort by provider name
                    self.providers = _.sortBy(self.providers, ["providerName"]);

                    if (self.providers.length === 0) {
                        self.data.staff_id = "0";
                    } else {
                        self.setStaffId(self.providers);
                    }
                });
        },
        getAuthors() {
            let self = this;
            return self.$http
                .get(process.env.BARRAMUNDI + "actor/staff", {
                    params: {
                        getAll: 1
                    }
                })
                .then(function (response) {
                    self.authors = response.data.response.resources || [];
                    self.authors = _.map(self.authors, element => ({
                        ...element,
                        id: element.actor_id,
                        authorName: element.firstname + " " + element.lastname
                    }));
                    // --- sort by author name
                    self.authors = _.sortBy(self.authors, ["authorName"]);

                    if (self.authors.length === 0) {
                        self.data.author_id = "0";
                    } else {
                        self.setAuthorId(self.authors);
                    }
                });
        },
        setStaffId(providerOptions) {
            let self = this;

            self.data.isDefaultProvider = false;
            let currentStaff = find(providerOptions, [
                "id",
                self.data.staff_id
            ]);
            let defaultClinicProvider = find(providerOptions, [
                "is_default_provider",
                true
            ]);
            if (!currentStaff) {
                let next = true;
                if (self.data.nonStaff) {
                    let nonStaffDefaultProviderId =
                        self.data.nonStaff.provider_id;
                    if (nonStaffDefaultProviderId) {
                        let providerStaff = find(providerOptions, [
                            "id",
                            nonStaffDefaultProviderId
                        ]);

                        if (providerStaff) {
                            next = false;
                            self.data.staff_id = providerStaff.id;
                        }
                    }
                }

                if (next) {
                    let providerStaff = find(providerOptions, [
                        "id",
                        this.$root.currentActor.id
                    ]);
                    if (providerStaff) {
                        self.data.staff_id = providerStaff.id;
                    } else if (defaultClinicProvider) {
                        // default the provider if cannot find provider
                        self.data.isDefaultProvider = true;
                        self.data.staff_id = defaultClinicProvider.id;
                    } else {
                        self.data.staff_id = providerOptions[0].id;
                    }
                }
            } else {
                if (defaultClinicProvider) {
                    self.data.isDefaultProvider =
                        self.data.staff_id == defaultClinicProvider.id;
                }
            }
        },
        setAuthorId(authorOptions) {
            let self = this;
            let currentAuthor = find(authorOptions, [
                "id",
                self.data.author_id
            ]);

            if (!currentAuthor) {
                let providerStaff = find(authorOptions, [
                    "id",
                    this.$root.currentActor.id
                ]);
                if (providerStaff) {
                    self.data.author_id = providerStaff.id;
                } else {
                    self.data.author_id = authorOptions[0].id;
                }
            } else {
                self.data.author_id == currentAuthor;
            }
        },
        checkStaffIdInProviders() {
            // safety measure to make sure that staff is inside the provider list
            let staffIsProvider = find(this.providers, [
                "id",
                this.data.staff_id
            ]);
            return staffIsProvider;
        },
        getReferrer() {
            let self = this;
            return self.$http
                .get(
                    process.env.ACTOR_SERVICE +
                        "client/" +
                        self.data.client_id +
                        "/referrer"
                )
                .then(function (response) {
                    return response.data.response.resource;
                });
        },
        getWeight(patient_id) {
            let self = this;
            return this.$http
                .get(
                    process.env.BARRAMUNDI + "patient/" + patient_id + "/vital"
                )
                .then(function (response) {
                    self.vital = response.data.response.resource;
                    if (self.vital) {
                        // self.data.weight = self.vital.value;
                        // self.data.uom = self.vital.master_uom_id;
                    }
                });
        },
        getLocations(client_id) {
            let self = this;
            return this.$http
                .get(
                    process.env.BARRAMUNDI +
                        "actor/client/" +
                        client_id +
                        "/location"
                )
                .then(function (response) {
                    self.locations = response.data.response.resources;

                    if (self.data.appointmentLocation) {
                        self.locations.forEach(element => {
                            if (
                                self.data.appointmentLocation.location_id ==
                                element.id
                            ) {
                                if (self.$root.setting.location == 0) {
                                    self.data.location = null;
                                } else {
                                    self.data.location = element;
                                }
                            }
                        });
                    } else {
                        if (self.$root.setting.location == 0) {
                            self.data.location = null;
                        } else if (self.$root.setting.location == 1) {
                            self.data.location = self.locations[0];
                        }
                    }
                });
        },
        getBilling(client_id) {
            let self = this;

            return Promise.all([
                this.$http
                    .get(
                        process.env.ACTOR_SERVICE +
                            "client/" +
                            client_id +
                            "/estimate"
                    )
                    .then(function (response) {
                        self.estimates = response.data.response.resource;
                    }),

                this.$http
                    .get(
                        process.env.ACTOR_SERVICE +
                            "client/" +
                            client_id +
                            "/invoice"
                    )
                    .then(function (response) {
                        self.invoices = response.data.response.resource;
                    })
            ]).then(() => {
                if (self.data.bill_to_invoice.id) {
                    self.data.bill_to_invoice = find(self.billings, {
                        id: self.data.bill_to_invoice.id
                    });
                }

                if (self.check_in_billing === 3) {
                    self.data.bill_to_invoice =
                        self.invoices.length > 0
                            ? find(self.billings, {
                                  id: self.invoices[0].id
                              })
                            : -1;
                }
            });
        },
        clientChange(client) {
            let self = this;
            self.data.client_id = client ? client.id : null;
            self.data.type = client ? 0 : 1;
            this.client_id = self.data.client_id;
        },
        referrerChange(referrer) {
            if (referrer) {
                this.data.referrer = referrer;
                this.data.referral_id = this._bindReferrer(referrer);
            } else {
                this.data.referrer = null;
                this.data.referral_id = null;
            }
        },
        clientFocus() {
            let self = this;
            if (!self.cOptions.length && self.data.patient_id) {
                this.$http
                    .get(
                        process.env.BARRAMUNDI +
                            "patient/" +
                            self.data.patient_id +
                            "/client"
                    )
                    .then(function (response) {
                        self.cOptions = response.data.response.resources;
                    });
            }
        },
        billingNote(bill) {
            return (
                (bill.type === -2 ? "Estimate" : "Invoice") +
                " #" +
                bill.index +
                " (" +
                moment(bill.start.date)
                    .tz(this._timeZone)
                    .format(this.datePickerOptions.format) +
                ")"
            );
        },
        setDefault(appointmentType) {
            if (appointmentType) {
                if (this.data.patient && appointmentType.bundle_template_id) {
                    if (!this.data.single_bundle_id) {
                        this.data.single_bundle_id =
                            appointmentType.bundle_template_id;
                    }

                    if (
                        this.data.bundle_id.length <= 0 &&
                        this.bundles.length
                    ) {
                        this.data.bundle_id = [
                            find(this.bundles, {
                                id: appointmentType.bundle_template_id
                            })
                        ];
                    }
                }

                if (
                    this.data.patient &&
                    !this.data.soap_id &&
                    appointmentType.soap_template_id
                ) {
                    this.data.soap_id = appointmentType.soap_template_id;
                }

                if (this.data.soap_id) {
                    this.data.soap_id = this.data.soap_id.replace(/\0/g, "");
                }

                if (appointmentType.checkin_webforms_templates) {
                    this.data.webforms = appointmentType.checkin_webforms_templates.map(
                        webformTemplate => {
                            return {
                                id: webformTemplate.template.id,
                                name: webformTemplate.template.name
                            };
                        }
                    );
                }

                this.selectedCommunicationType = find(this.sendViaOptions, {
                    value: 0
                });

                if (this.data.client) {
                    if (
                        !isEmpty(this.data.client.mobile) &&
                        this.checkExtensionsSMSEnabled()
                    ) {
                        this.selectedCommunicationType = find(
                            this.sendViaOptions,
                            { value: COMMUNICATION_TYPES.SMS }
                        );
                    } else if (!isEmpty(this.data.client.email)) {
                        this.selectedCommunicationType = find(
                            this.sendViaOptions,
                            { value: COMMUNICATION_TYPES.EMAIL }
                        );
                    }
                }
                this.data.communicationType = this.selectedCommunicationType.value;

                this.setSendWebformViaField(
                    this.selectedCommunicationType,
                    this.data.client
                );
            }
        },
        isDirty() {
            const a = function (e) {
                return e === true;
            };
            return this.$validator.fields
                .map(field => field.flags.dirty)
                .some(a);
        },
        checkAuthorIdInAuthors() {
            let authorInAuthors = (authorInAuthors = find(this.authors, [
                "id",
                this.data.author_id
            ]));
            return authorInAuthors;
        },
        setSendWebformViaField(communicationType, client) {
            let type = communicationType?.value;

            this.data.mobileCountryCode = null;
            this.data.mobile = null;
            this.data.isUpdateClientContact = false;
            this.data.email = null;
            if (type && client) {
                if (type == COMMUNICATION_TYPES.EMAIL) {
                    this.data.email = this.data.client.email;
                    this.data.isUpdateClientContact = isEmpty(this.data.email);
                } else if (type == COMMUNICATION_TYPES.SMS) {
                    this.data.mobileCountryCode = this.data.client.mobile_country_code;
                    this.data.mobile = this.data.client.mobile;
                    this.data.isUpdateClientContact = isEmpty(this.data.mobile);
                }
            }
        },
        async getTriageList() {
            await TriageService.getTriageTypeLists()
                .then(res => {
                    if (res && res.code === 200) {
                        this.triageOptions = res.resources;
                        this.setTriageId();
                    } else {
                        throw new Error(
                            "Error: Unable to get the triage list."
                        );
                    }
                })
                .catch(e => {
                    console.error(e);
                    self._message(
                        "Error: Unable to get the triage list.",
                        "danger"
                    );
                });
        },
        setTriageId() {
            if (this.data && this.data.active_triage) {
                this.data.triage_id = this.data?.active_triage?.id;
                this.data.triage_type_id = this.data?.active_triage?.triage_type_id;
            } else {
                // create new triage with default triage_type = 5
                this.data.triage_id = null;
                this.data.triage_type_id = 5;
            }
        }
    }
};
</script>
